import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {environment} from '../environments/environment';
import {Pagination, PaginationParams} from '../model/pagination';
import {LocationDto} from '../model/continent';
import {cleaningParams} from '../functions/cleaningParams';

@Injectable({
  providedIn: 'root'
})
export class CountryService {
  constructor(private httpClient: HttpClient) {}

  private country: BehaviorSubject<LocationDto> = new BehaviorSubject<LocationDto>(new LocationDto());
  private currentIndex: number = 0;
  countries: LocationDto[] = [];


  getCountryList(params: PaginationParams): Observable<Pagination<LocationDto>> {
    const cleanParams = cleaningParams(params);
    return this.httpClient.get<Pagination<LocationDto>>(`${environment.apiUrl}/public/country`,
      { params: { ...cleanParams } });
  }

  getCountryListByAdventureId(params: PaginationParams, id: number): Observable<Pagination<LocationDto>> {
    const cleanParams = cleaningParams(params);
    return this.httpClient.get<Pagination<LocationDto>>(`${environment.apiUrl}/public/country/by-adventure/${id}`,
      { params: { ...cleanParams } });
  }

  setAndShuffleCountries(countries: LocationDto[]) {
    this.countries = this.shuffleArray([...countries]);
    this.currentIndex = 0;
  }

  private shuffleArray(array: LocationDto[]): LocationDto[] {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }

  getCountryListByContinent(params: PaginationParams, id: number): Observable<Pagination<LocationDto>> {
    const cleanParams = cleaningParams(params);
    return this.httpClient.get<Pagination<LocationDto>>(`${environment.apiUrl}/public/country/by-continent/`+ id,
      { params: { ...cleanParams } });
  }

  getCountry(id: number): Observable<LocationDto> {
    return this.httpClient.get<LocationDto>(`${environment.apiUrl}/public/country/${id}`);
  }

  getCountryByUrl(url: string): Observable<LocationDto> {
    return this.httpClient.get<LocationDto>(`${environment.apiUrl}/public/country/by-url/${url}`);
  }

  moveUp(id: number): Observable<boolean> {
    return this.httpClient.put<boolean>(`${environment.apiUrl}/country/move-up/${id}`, id);
  }

  moveDown(id: number): Observable<boolean> {
    return this.httpClient.put<boolean>(`${environment.apiUrl}/country/move-down/${id}`, id);
  }

  saveCountryPhoto(id: number, file: File) {
    const correctedFile = new File(
      [file],
      file.name.replace(/[\s()]/g, ''),
      { type: file.type }
    );
    const formData = new FormData();
    formData.append("file", correctedFile);
    return this.httpClient.post<LocationDto>(`${environment.apiUrl}/country/file/${id}`, formData);
  }

  saveCountryFooterPhoto(id: number, file: File) {
    const correctedFile = new File(
      [file],
      file.name.replace(/[\s()]/g, ''),
      { type: file.type }
    );
    const formData = new FormData();
    formData.append("file", correctedFile);
    return this.httpClient.post<LocationDto>(`${environment.apiUrl}/country/footer/file/${id}`, formData);
  }

  saveCountryIcon(id: number, file: File) {
    const correctedFile = new File(
      [file],
      file.name.replace(/[\s()]/g, ''),
      { type: file.type }
    );
    const formData = new FormData();
    formData.append("file", correctedFile);
    return this.httpClient.post<LocationDto>(`${environment.apiUrl}/country/icon/file/${id}`, formData);
  }

  saveCountryIconActive(id: number, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return this.httpClient.post<LocationDto>(`${environment.apiUrl}/country/icon-active/file/${id}`, formData);
  }

  saveCountryAdditionalPhoto(id: number, file: File) {
    const correctedFile = new File(
      [file],
      file.name.replace(/[\s()]/g, ''),
      { type: file.type }
    );
    const formData = new FormData();
    formData.append("file", correctedFile);
    return this.httpClient.post<LocationDto>(`${environment.apiUrl}/country/additional-file/${id}`, formData);
  }

  saveCountryCarousel(idCountry: number, idCarousel: number, file: File) {
    const correctedFile = new File(
      [file],
      file.name.replace(/[\s()]/g, ''),
      { type: file.type }
    );
    const formData = new FormData();
    formData.append("file", correctedFile);
    return this.httpClient.post<LocationDto>(`${environment.apiUrl}/country/file/${idCountry}/${idCarousel}`, formData);
  }

  deleteCountryCarousel(idCountry: number, idCarousel: number) {
    return this.httpClient.delete(`${environment.apiUrl}/country/${idCountry}/${idCarousel}`);
  }

  saveCountry(Country: LocationDto): Observable<LocationDto> {
    return this.httpClient.post<LocationDto>(`${environment.apiUrl}/country`, Country);
  }

  delete(id: number) {
    return this.httpClient.delete(`${environment.apiUrl}/country/${id}`);
  }

  saveEventPhoto(idCountry: number, eventId: number, file: File) {
    const correctedFile = new File(
      [file],
      file.name.replace(/[\s()]/g, ''),
      { type: file.type }
    );
    const formData = new FormData();
    formData.append("file", correctedFile);
    return this.httpClient.post<LocationDto>(`${environment.apiUrl}/country/file/event/${idCountry}/${eventId}`, formData);
  }

  deleteEventCountry(idCountry: number, eventId: number) {
    return this.httpClient.delete(`${environment.apiUrl}/country/event/${idCountry}/${eventId}`);
  }

  setRandom() {
    if (this.countries.length === 0) {
      this.country.next(new LocationDto());
      return;
    }
    this.currentIndex = (this.currentIndex + 1) % this.countries.length;
    this.country.next(this.countries[this.currentIndex]);
  }

  getRandom(): LocationDto {
    if (this.countries.length === 0) {
      return new LocationDto();
    }
    if (this.currentIndex > this.countries.length) {
      this.currentIndex = 0;
    }
    return this.countries[this.currentIndex];
  }
}
